import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPerson, MdCheck, MdLocationOn, MdCameraAlt, MdVisibility, MdEdit } from 'react-icons/md';
import RepProfileEditForm from './RepProfileEditForm';
import VerifiedBadge from '../VerifiedBadge';
import VerificationSection from './VerificationSection';

function RepProfileViewMode({
  profile,
  offices,
  editingProfile,
  editData,
  submitting,
  onStartEditing,
  onEditChange,
  onSaveProfile,
  onCancelEdit,
  onEditBio,
  onSaveBio,
  editingBio,
  bioText,
  onBioTextChange,
  onCancelBio,
  sanitizeBioInput,
  onOpenPictureCropper,
  onCloseProfile,
}) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        {/* Profile Picture with Upload Button */}
        <div className="relative group">
          <div className="w-20 aspect-square rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-gray-300">
            {profile.profile_picture_url ? (
              <img src={profile.profile_picture_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <MdPerson className="text-4xl text-gray-400" />
            )}
          </div>
          <button
            onClick={onOpenPictureCropper}
            className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            title="Change profile picture"
            data-testid="change-picture-btn"
          >
            <MdCameraAlt className="text-white text-2xl" />
          </button>
          <button
            onClick={onOpenPictureCropper}
            className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-primary-dark transition-colors"
            title="Upload picture"
          >
            <MdCameraAlt className="text-xs" />
          </button>
        </div>
        <div className="flex-grow">
          {editingProfile ? (
            <RepProfileEditForm
              editData={editData}
              offices={offices}
              submitting={submitting}
              onEditChange={onEditChange}
              onSave={onSaveProfile}
              onCancel={onCancelEdit}
            />
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 flex items-center gap-1">
                  {profile.first_name} {profile.last_name}
                  <VerifiedBadge status={profile.verification_status} size="md" />
                </h3>
                <button
                  onClick={onStartEditing}
                  className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-md transition-colors"
                  title="Edit profile details"
                  data-testid="edit-profile-btn"
                >
                  <MdEdit className="text-base" />
                </button>
              </div>
              {profile.office && (
                <p className="text-sm text-gray-600">{profile.office.position_title}</p>
              )}
              {(profile.running_state || profile.running_city_county) && (
                <p className="text-sm text-blue-600 flex items-center gap-1">
                  <MdLocationOn className="text-sm" />
                  {profile.running_city_county ? `${profile.running_city_county}, ${profile.running_state}` : profile.running_state}
                </p>
              )}
              <p className="text-sm text-gray-500">{profile.primary_email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Editable Biography */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Biography</span>
          {!editingBio && (
            <button
              onClick={onEditBio}
              className="text-xs text-primary hover:text-primary-dark font-medium transition-colors"
              data-testid="edit-bio-btn"
            >
              {profile.biography ? 'Edit' : 'Add Bio'}
            </button>
          )}
        </div>
        {editingBio ? (
          <div>
            <textarea
              value={bioText}
              onChange={(e) => onBioTextChange(sanitizeBioInput(e.target.value))}
              maxLength={2000}
              rows={4}
              placeholder="Tell voters about yourself, your experience, and your platform..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
              data-testid="bio-textarea"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{bioText.length}/2000</span>
              <div className="flex gap-2">
                <button
                  onClick={onCancelBio}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                  data-testid="cancel-bio-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={onSaveBio}
                  disabled={submitting}
                  className="px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                  data-testid="save-bio-btn"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {profile.biography || <span className="italic text-gray-400">No biography yet. Click "Add Bio" to tell voters about yourself.</span>}
          </p>
        )}
      </div>

      {/* Identity Verification */}
      <VerificationSection profile={profile} />

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <span className="text-sm text-green-600 flex items-center gap-1">
          <MdCheck /> Active Profile
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/representatives/${profile.profile_id}`)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-primary border border-primary/30 hover:bg-primary/5 rounded-lg transition-colors font-medium"
            data-testid="view-public-profile-section-btn"
          >
            <MdVisibility className="text-base" />
            View Public Profile
          </button>
          <button
            onClick={onCloseProfile}
            className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Close Profile
          </button>
        </div>
      </div>
    </div>
  );
}

export default RepProfileViewMode;
